
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';

const moduleContent = [
  {
    title: 'Sustaining Meaning and Life Satisfaction',
    content: [
      '<strong>Objective:</strong> Learn how to maintain a sense of fulfillment and purpose amid life\'s changes.',
      '<br/>',
      '<strong>Discussion:</strong> Meaning fluctuates – it grows when you nurture it and fades when neglected.',
      'Reflection, gratitude, and purpose-tracking can help maintain a "life satisfaction equilibrium."'
    ]
  },
  {
    title: 'Topics Covered',
    content: [
      '• The role of gratitude and mindfulness in sustaining meaning',
      '• Reframing challenges as growth opportunities',
      '• Designing your personal "Life Fulfillment Plan"'
    ]
  },
  {
    title: 'Activity',
    content: [
      'Create your Meaning Maintenance Plan:',
      'Daily: Gratitude journaling (3 things I\'m thankful for)',
      'Weekly: Reflection question – "How did I live my values this week?"',
      'Monthly: Check your satisfaction and realign goals'
    ]
  },
  {
    title: 'Key Takeaway',
    content: [
      'Meaningful living is a lifelong practice. You are both the author and the artist of your legacy.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 5: Sustaining Meaning and Life Satisfaction! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about sustaining meaning and satisfaction.',
        'The quiz will help you review key concepts about maintaining fulfillment.'
      ]
    }
  }
];

const LegendarialModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('legendarial5');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/legendarial-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/legendarial-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left legendarial-panel">
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Sustaining Meaning and Life Satisfaction</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Designing a Life of Meaning, Fulfillment, and Legacy
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph, index) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/legendarial-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegendarialModule5Page;


